import { HoneycombWebSDK } from '@honeycombio/opentelemetry-web';
import { Attributes } from '@opentelemetry/api';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { InMemorySpanExporter, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { Foldable, chain, chainWithIndex } from 'fp-ts/Array';
import { fromFoldable, toArray } from 'fp-ts/Record';
import { last } from 'fp-ts/Semigroup';
import { pipe } from 'fp-ts/function';

import { Environment } from '@flywire/internal-components/model';

// eslint-disable-next-line @typescript-eslint/no-namespace, @typescript-eslint/prefer-namespace-keyword
declare module globalThis {
  let OTEL_IN_MEMORY_EXPORTER_INSTANCE: InMemorySpanExporter | undefined;
  let OTEL_IN_MEMORY_PROCESSOR_INSTANCE: SpanProcessor | undefined;
  let FLYWIRE_FLOTEL_JS_INSTANCE: HoneycombWebSDK | undefined;
}

const buildCollectorEndpoint = (env: Environment) => {
  if (env === 'development') {
    return '/v1/traces';
  } else if (env === 'staging') {
    return 'https://otel-collector-browser.flywire.lol';
  } else if (env === 'demo-production' || env === 'production') {
    return 'https://otel-collector-browser.flywire.com';
  } else {
    return 'https://otel-collector-browser.flywire.cc';
  }
};

const bearerToken = (env: Environment) => {
  if (env === 'demo-production' || env === 'production') {
    return 'LagartusEdus021';
  } else if (env === 'staging') {
    return 'Lagart021';
  } else {
    return 'hola';
  }
};

export const initializeTracing = ({
  serviceName,
  env,
  attributes = {}
}: {
  serviceName: string;
  env: Environment;
  attributes?: Attributes;
}) => {
  const configDefaults = {
    ignoreNetworkEvents: true,
    propagateTraceHeaderCorsUrls: [
      /payment-checkout/g,
      /elements/g,
      /flywire-api/g,
      /api/g,
      /localhost/g
    ]
    // we cannot whitelist `onplanprocessing` as they need to accept the traceparent header
  };

  const inTestMode = env === 'test';

  if (inTestMode && !globalThis.OTEL_IN_MEMORY_EXPORTER_INSTANCE) {
    throw new Error(
      `The global variable \`OTEL_IN_MEMORY_EXPORTER_INSTANCE\` and \`OTEL_IN_MEMORY_PROCESSOR_INSTANCE\` must be exported with a new InMemorySpanExporter instance from the @opentelemetry/api package.
      Example:

      globalThis.OTEL_IN_MEMORY_EXPORTER_INSTANCE = new InMemorySpanExporter();
      globalThis.OTEL_IN_MEMORY_PROCESSOR_INSTANCE = new SimpleSpanProcessor(
        globalThis.OTEL_IN_MEMORY_EXPORTER_INSTANCE
      );

      If the test framework in question is jest, please refer to their document page: https://jestjs.io/docs/configuration#globalsetup-string on how to set up a global variable
      `
    );
  }

  const sdk = new HoneycombWebSDK({
    debug: env === 'development',
    endpoint: buildCollectorEndpoint(env),
    headers: {
      Authorization: `Bearer ${bearerToken(env)}`
    },
    serviceName,
    resourceAttributes: { ...attributes, 'deployment.environment': env },
    skipOptionsValidation: true, // because we are not including apiKey we need to skip it
    contextManager: new ZoneContextManager(),
    instrumentations: !inTestMode
      ? [
          getWebAutoInstrumentations({
            '@opentelemetry/instrumentation-xml-http-request': configDefaults,
            '@opentelemetry/instrumentation-fetch': configDefaults,
            '@opentelemetry/instrumentation-document-load': configDefaults,
            '@opentelemetry/instrumentation-user-interaction': {
              enabled: false
            }
          })
        ]
      : [],
    localVisualizations: env === 'development',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    spanProcessors: inTestMode ? [globalThis.OTEL_IN_MEMORY_PROCESSOR_INSTANCE as any] : undefined
  });

  // There's no way to remove the default exporters.
  // More info: https://flywire.slack.com/archives/C04T7NW39AN/p1725288568063159
  if (inTestMode) {
    sdk['_tracerProviderConfig']['spanProcessor']['spanProcessors'] =
      sdk['_tracerProviderConfig']['spanProcessor']['spanProcessors'].slice(-1);
  }

  sdk.start();

  // There can only be one instace running
  globalThis.FLYWIRE_FLOTEL_JS_INSTANCE = sdk;
  return sdk;
};

type Primitive = string | number | boolean | null | undefined;

export const buildAttributes = <T extends object>(
  buildAttributes: T,
  prefix: string = 'app'
): Attributes => {
  const flatten = (item: T, path: string): Array<[string, Primitive]> =>
    typeof item === 'object' && item !== null
      ? Array.isArray(item)
        ? pipe(
            item,
            chainWithIndex((i, v) => flatten(v, `${path}[${i}]`))
          )
        : pipe(
            toArray(item as T),
            chain(([k, v]) => flatten(v, `${path}.${k}`))
          )
      : [[path, item as Primitive]];

  return pipe(
    flatten(buildAttributes, prefix),
    fromFoldable(last<Primitive>(), Foldable)
  ) as Attributes;
};
